import type { HttpErrorResponse } from '@angular/common/http';

import { createSelector } from '@ngrx/store';

import {
  CustomFieldItemCreatePayload,
  CustomFieldItem,
  OfferAssignment,
  RequisitionAssignment,
} from '@recruitee/custom-fields-types';
import {
  createEntityCollectionStore,
  getEntityCollectionActions,
  ID,
  pipeState,
  ngrxEvent,
} from '@recruitee/ngrx';

const { actions, namespace, selectors, reducer } = createEntityCollectionStore<CustomFieldItem>()(
  'CustomFields',
  {
    customActions: adapter => {
      const defaultActions = getEntityCollectionActions(adapter, ({ id }) => id!);

      return {
        createOnePessimistic: (state, payload: { data: CustomFieldItemCreatePayload }) =>
          adapter.markAsPending(state),
        updateOnePessimistic: (state, payload: { data: CustomFieldItemCreatePayload; id: ID }) =>
          adapter.markAsPending(state),
        duplicate: (state, payload: { data: ID }) => adapter.markAsPending(state),
        duplicateSuccess: (state, payload: { data: CustomFieldItem }) =>
          pipeState(
            state,
            next => adapter.addOne(next, payload.data),
            next => adapter.markAsLoaded(next),
          ),
        duplicateFailed: defaultActions.createOnePessimisticFailed,
        fetchAssignedJobList: ngrxEvent<{
          data: number;
        }>(),
        fetchAssignedJobListSuccess: ngrxEvent<{
          data: {
            assignedOffers: OfferAssignment[];
          };
        }>(),
        fetchAssignedJobListFailed: ngrxEvent<{ error: HttpErrorResponse }>(),
        fetchAssignedJobTemplateList: ngrxEvent<{
          data: number;
        }>(),
        fetchAssignedJobTemplateListSuccess: ngrxEvent<{
          data: {
            assignedOfferTemplates: OfferAssignment[];
          };
        }>(),
        fetchAssignedJobTemplateListFailed: ngrxEvent<{ error: HttpErrorResponse }>(),
        fetchAssignedRequisitionList: ngrxEvent<{
          data: number;
        }>(),
        fetchAssignedRequisitionListSuccess: ngrxEvent<{
          data: {
            assignedRequisitions: RequisitionAssignment[];
          };
        }>(),
        fetchAssignedRequisitionListFailed: ngrxEvent<{ error: HttpErrorResponse }>(),
      };
    },
  },
);

const CustomFieldsActions = {
  fetchCollection: actions.fetchCollection,
  fetchCollectionSuccess: actions.fetchCollectionSuccess,
  fetchCollectionFailed: actions.fetchCollectionFailed,
  createOnePessimistic: actions.createOnePessimistic,
  createOnePessimisticSuccess: actions.createOnePessimisticSuccess,
  createOnePessimisticFailed: actions.createOnePessimisticFailed,
  updateOnePessimistic: actions.updateOnePessimistic,
  updateOnePessimisticSuccess: actions.updateOnePessimisticSuccess,
  updateOnePessimisticFailed: actions.updateOnePessimisticFailed,
  removeOnePessimistic: actions.removeOnePessimistic,
  removeOnePessimisticSuccess: actions.removeOnePessimisticSuccess,
  removeOnePessimisticFailed: actions.removeOnePessimisticFailed,
  duplicate: actions.duplicate,
  duplicateSuccess: actions.duplicateSuccess,
  duplicateFailed: actions.duplicateFailed,
  fetchAssignedJobList: actions.fetchAssignedJobList,
  fetchAssignedJobListSuccess: actions.fetchAssignedJobListSuccess,
  fetchAssignedJobListFailed: actions.fetchAssignedJobListFailed,
  fetchAssignedJobTemplateList: actions.fetchAssignedJobTemplateList,
  fetchAssignedJobTemplateListSuccess: actions.fetchAssignedJobTemplateListSuccess,
  fetchAssignedJobTemplateListFailed: actions.fetchAssignedJobTemplateListFailed,
  fetchAssignedRequisitionList: actions.fetchAssignedRequisitionList,
  fetchAssignedRequisitionListSuccess: actions.fetchAssignedRequisitionListSuccess,
  fetchAssignedRequisitionListFailed: actions.fetchAssignedRequisitionListFailed,
};

const CustomFieldsSelectors = {
  ...selectors,
  availableInRequisitions: createSelector(selectors.allEntities, data =>
    data.filter(field => !!field.assignToRequisition),
  ),
};

export {
  CustomFieldsActions,
  namespace as CustomFieldsNamespace,
  reducer as CustomFieldsReducer,
  CustomFieldsSelectors,
};
